import { useAuth } from 'context/auth';
import { useCustomLocation } from 'context/customLocation';
import { useListsContext } from 'context/lists';
import { FeConstants } from 'constants/FeConstants';
import { ListType } from 'crono-fe-common/types/list';
import usePatchUserPreferences from 'hooks/services/user/usePatchUserPreferences';
import { getListsPageTab } from './listsPageTab';

const STORAGE_KEY_BY_LIST_TYPE: Partial<Record<ListType, string>> = {
  [ListType.Account]: FeConstants.LIST_STORAGE_KEYS.ACCOUNT,
  [ListType.Prospect]: FeConstants.LIST_STORAGE_KEYS.PROSPECT,
  [ListType.Lead]: FeConstants.LIST_STORAGE_KEYS.LEAD,
  [ListType.Template]: FeConstants.LIST_STORAGE_KEYS.TEMPLATE,
  [ListType.Strategy]: FeConstants.LIST_STORAGE_KEYS.STRATEGY,
};

export const useOpenList = () => {
  const { user } = useAuth();
  const { setCustomStateLocation } = useCustomLocation();
  const { setCurrentListId } = useListsContext();
  const { mutateAsync: patchUserPreferencesAsync } = usePatchUserPreferences();

  const addListToOpenLists = async (listId: number) => {
    const currentOpenLists: number[] =
      user?.userPreferences?.openListsIds || [];
    if (currentOpenLists.includes(listId)) {
      return;
    }
    const newOpenLists: number[] = [listId, ...currentOpenLists];
    await patchUserPreferencesAsync({
      openListsIds: newOpenLists,
    });
    if (user) {
      user.userPreferences.openListsIds = newOpenLists;
    }
  };

  const selectListsPageTab = (type: ListType | null) => {
    const tab = getListsPageTab(type);
    if (tab) {
      setCustomStateLocation({ listsPageTabState: tab });
    }
  };

  /**
   * The list tabs only keep a selection that is among the user's open lists and
   * re-read it from localStorage whenever the tab type changes, so both have to
   * be in place before the page that shows the list renders.
   *
   * Resolves to false when the list couldn't be added to the open lists: the
   * page then falls back to "All", but callers still get to navigate.
   */
  const openList = async (
    listId: number,
    type: ListType | null,
  ): Promise<boolean> => {
    selectListsPageTab(type);
    let addedToOpenLists = true;
    try {
      await addListToOpenLists(listId);
    } catch {
      addedToOpenLists = false;
    }
    setCurrentListId(listId);

    const storageKey = type ? STORAGE_KEY_BY_LIST_TYPE[type] : undefined;
    if (storageKey) {
      localStorage.setItem(storageKey, String(listId));
    }
    return addedToOpenLists;
  };

  return { selectListsPageTab, openList };
};
